import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, HelpCircle } from 'lucide-react';
interface FAQItem {
  question: string;
  answer: string;
}
interface FAQSection {
  title: string;
  items: FAQItem[];
}
const sections: FAQSection[] = [{
  title: 'Orders & Payment',
  items: [{
    question: 'What payment methods do you accept?',
    answer: 'All payments are processed through the Bank of Maldives (BML) Payment Gateway. You can pay with any BML-issued debit or credit card, as well as Visa and Mastercard from other banks.'
  }, {
    question: 'Is my payment information secure?',
    answer: 'Yes. We never store your card details. All transactions are processed directly on the BML secure gateway, which is fully PCI-DSS compliant and encrypts every transaction.'
  }, {
    question: 'Can I pay on delivery?',
    answer: 'Cash on delivery is not currently available. All orders must be paid online via the BML gateway at checkout.'
  }, {
    question: 'Will I receive an order confirmation?',
    answer: 'Yes. As soon as your payment is processed, you will see an order confirmation on screen with your order number (e.g. BS-12345), and a receipt will be sent to the email address you provided at checkout.'
  }]
}, {
  title: 'Shipping & Delivery',
  items: [{
    question: 'Do you deliver across the Maldives?',
    answer: 'Yes. We deliver to all inhabited islands. Standard delivery to Malé and Hulhumalé typically takes 1–2 business days. Other islands take 3–7 business days depending on ferry and seaplane schedules.'
  }, {
    question: 'How much does shipping cost?',
    answer: 'Standard delivery is free on orders over MVR 500. Below that, standard delivery is MVR 30 within Greater Malé and MVR 80 to other islands. Express delivery is MVR 50 and available for Malé and Hulhumalé only.'
  }, {
    question: 'Can I track my order?',
    answer: 'Yes. Once your order has been dispatched, you will receive an SMS and email with a tracking link. You can also enter your order number on the Track Order page.'
  }, {
    question: 'Do you ship internationally?',
    answer: 'Not at this time. We currently only ship within the Maldives. For wholesale or bulk export enquiries, please email partners@beachshop.mv.'
  }]
}, {
  title: 'Returns & Refunds',
  items: [{
    question: 'What is your return policy?',
    answer: 'We accept returns within 14 days of delivery for unworn, unused items in original packaging. Swimwear and intimate items can only be returned if the hygiene sticker is intact. Sale items are final sale.'
  }, {
    question: 'How do I start a return?',
    answer: 'Email returns@beachshop.mv with your order number and the reason for return. We will respond within 24 hours with return instructions.'
  }, {
    question: 'When will I get my refund?',
    answer: 'Once we receive and inspect your returned item, refunds are processed back to your original payment method within 5–7 business days via the BML gateway.'
  }]
}, {
  title: 'Products',
  items: [{
    question: 'Are your sunscreens really reef-safe?',
    answer: 'Yes. All sunscreens we sell are mineral-based (zinc oxide / titanium dioxide) and free from oxybenzone and octinoxate, which are harmful to coral reefs. We will never sell anything that damages our reefs.'
  }, {
    question: 'Do products come in different sizes?',
    answer: 'Most apparel and footwear is available in a range of sizes. Available sizes for each item are shown on the product page, and out-of-stock sizes appear crossed out. If your size is unavailable, contact us to be notified when it is back in stock.'
  }, {
    question: 'Can I customise a product?',
    answer: 'Many of our items support extras like monogram embroidery or gift wrap. These appear as add-ons on the product page with the additional price shown clearly before you add to cart.'
  }]
}];
function FAQAccordionItem({
  item,
  isOpen,
  onToggle
}: {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-478">
      <button type="button" onClick={onToggle} aria-expanded={isOpen} className="w-full flex items-center justify-between gap-4 py-4 text-left focus:outline-none" data-id="element-479">
        <span className="text-sm sm:text-base font-medium text-gray-900" data-id="element-480">
          {item.question}
        </span>
        <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-481" />
      </button>
      {isOpen && <p className="pb-4 pr-9 text-sm text-gray-600 leading-relaxed" data-id="element-482">
          {item.answer}
        </p>}
    </div>;
}
export function FAQ() {
  const [openKey, setOpenKey] = useState<string | null>('Orders & Payment-0');
  return <div className="bg-gray-50 min-h-screen pb-16" data-id="element-483">
      {/* Hero */}
      <section className="bg-white border-b border-gray-200" data-id="element-484">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-id="element-485">
          <span className="text-xs font-semibold uppercase tracking-widest text-red-600" data-id="element-486">
            Help Centre
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-3 mb-4" data-id="element-487">
            Frequently Asked Questions
          </h1>
          <p className="text-gray-600 text-base sm:text-lg leading-relaxed" data-id="element-488">
            Quick answers about orders, delivery, returns, and our products. If
            you can't find what you're looking for, get in touch — we're happy
            to help.
          </p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-6" data-id="element-489">
        {sections.map(section => <div key={section.title} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-490">
            <div className="px-6 pt-6 pb-2" data-id="element-491">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-492">
                {section.title}
              </h2>
            </div>
            <div className="px-6 pb-2" data-id="element-493">
              {section.items.map((item, idx) => {
            const key = `${section.title}-${idx}`;
            return <FAQAccordionItem key={key} item={item} isOpen={openKey === key} onToggle={() => setOpenKey(openKey === key ? null : key)} data-id="element-494" />;
          })}
            </div>
          </div>)}

        {/* Still need help */}
        <div className="bg-red-50 border border-red-100 rounded-2xl p-6 flex items-start gap-4" data-id="element-495">
          <div className="w-10 h-10 rounded-lg bg-red-600 text-white flex items-center justify-center flex-shrink-0" data-id="element-496">
            <HelpCircle className="w-5 h-5" data-id="element-497" />
          </div>
          <div className="flex-1" data-id="element-498">
            <h3 className="font-semibold text-gray-900 mb-1" data-id="element-499">
              Still have questions?
            </h3>
            <p className="text-sm text-gray-600 mb-3" data-id="element-500">
              Our team usually replies within a few hours during business days.
            </p>
            <Link to="/contact" className="inline-flex items-center text-sm font-medium text-red-600 hover:text-red-700" data-id="element-501">
              Contact Support →
            </Link>
          </div>
        </div>
      </div>
    </div>;
}